import { createPrng, type Prng } from '../rng.js';
import type {
  Action,
  GameState,
  MapDefinition,
  PlayerId,
  TerritoryId,
  TurnPhase,
} from '../types.js';
import {
  bestAdvance,
  bestFortify,
  mostPressuredOwned,
  topAttacks,
  topDeployTargets,
  type AttackChoice,
} from './heuristic.js';
import { DEFAULT_PERSONALITY, type AiPersonality } from './personality.js';
import { softmaxSample } from './sampler.js';

/**
 * Pick the AI's next action for the current state. Returns `null` when
 * the game is over or the current player owns nothing (shouldn't be
 * called in either case, but it's defensive).
 *
 * The driver loop calls this once per tick: every successful dispatch
 * fires the GameStore listener which re-schedules the next tick. So
 * this function only needs to choose ONE action.
 *
 * Strategy:
 *  - Deploy: enumerate offensive deploy candidates, soft-sample from
 *    the top 3 using personality.randomness as temperature; fall back
 *    to most-pressured if no tile yields a favorable post-deploy
 *    attack. Setup round (turn 0) only has 1 unit but the same scoring
 *    applies.
 *  - Attack: enumerate favorable attacks, soft-sample from the top 3;
 *    end phase if nothing beats the personality threshold. The AI
 *    won't take a coin-flip or worse matchup unless its threshold
 *    explicitly permits it (Aggressive accepts coin-flips; Cautious
 *    refuses).
 *  - Advance: usually commit `maxUnits`, but reserve a small stack at
 *    source when source still has enemy neighbors so the AI can launch
 *    a follow-up attack from the original tile (`bestAdvance`).
 *  - Fortify: drain a safe interior tile into the connected border
 *    tile under the most enemy pressure; end turn if no useful move.
 */
export function chooseAction(
  state: GameState,
  map: MapDefinition
): Action | null {
  if (state.status.kind === 'victory') return null;
  const player = state.turn.currentPlayer;
  const personality = personalityOf(state, player);

  switch (state.turn.phase) {
    case 'deploy': {
      if (state.turn.reinforcementsRemaining <= 0) {
        return { type: 'endPhase', playerId: player };
      }
      const candidates = topDeployTargets(
        state,
        map,
        player,
        state.turn.reinforcementsRemaining,
        TOP_K
      );
      const target: TerritoryId | null =
        candidates.length > 0
          ? softmaxSample(
              candidates.map((c) => ({ item: c.id, score: c.ev })),
              personality.randomness,
              deriveAiPrng(state)
            )
          : mostPressuredOwned(state, map, player);
      if (!target) return { type: 'endPhase', playerId: player };
      return {
        type: 'deploy',
        playerId: player,
        territory: target,
        units: state.turn.reinforcementsRemaining,
      };
    }

    case 'attack': {
      if (state.turn.attack.kind === 'awaitingAdvance') {
        const sub = state.turn.attack;
        const prng = deriveAiPrng(state);
        const units = bestAdvance(
          state,
          map,
          player,
          { from: sub.from, to: sub.to, minUnits: sub.minUnits, maxUnits: sub.maxUnits },
          (max) => prng.nextInt(max)
        );
        return {
          type: 'advance',
          playerId: player,
          from: sub.from,
          to: sub.to,
          units,
        };
      }
      const candidates = topAttacks(state, map, player, TOP_K);
      if (candidates.length === 0) return { type: 'endPhase', playerId: player };
      const choice: AttackChoice =
        candidates.length === 1
          ? candidates[0]!
          : softmaxSample(
              candidates.map((c) => ({ item: c, score: c.ev })),
              personality.randomness,
              deriveAiPrng(state)
            );
      const fromUnits = state.territories[choice.from]?.units ?? 0;
      const dice = Math.min(3, Math.max(1, fromUnits - 1)) as 1 | 2 | 3;
      return {
        type: 'attack',
        playerId: player,
        from: choice.from,
        to: choice.to,
        attackingDice: dice,
      };
    }

    case 'fortify': {
      if (state.turn.fortifyUsed) {
        return { type: 'endTurn', playerId: player };
      }
      const choice = bestFortify(state, map, player);
      if (!choice) return { type: 'endTurn', playerId: player };
      const fromUnits = state.territories[choice.from]?.units ?? 0;
      const moveUnits = fromUnits - 1;
      if (moveUnits <= 0) return { type: 'endTurn', playerId: player };
      return {
        type: 'fortify',
        playerId: player,
        from: choice.from,
        to: choice.to,
        units: moveUnits,
      };
    }
  }
}

// --- Sampling helpers ---

// Top-3 is enough to add play variety without letting the AI throw
// real EV away on a fourth-best option. With temperature 0.05
// (Cautious) the third candidate's probability is already sub-1%.
const TOP_K = 3;

const PHASE_SALT: Record<TurnPhase, number> = {
  deploy: 0x9e3779b1,
  attack: 0x85ebca77,
  fortify: 0xc2b2ae3d,
};

/**
 * Derive a deterministic AI-side Prng from state without consuming
 * `state.rngSeed` (which belongs to combat). Re-derived fresh each
 * call. The seed combines rngSeed (advances naturally as combat
 * resolves) with turn metadata so:
 *   - same state → same sample (replays are deterministic)
 *   - successive attacks within a turn see different samples (rngSeed
 *     advances on each combat)
 *   - the deploy / attack / fortify phases salt differently so the AI
 *     doesn't accidentally draw the same number for both this-turn
 *     deploy and this-turn first-attack.
 */
function deriveAiPrng(state: GameState): Prng {
  const phaseSalt = PHASE_SALT[state.turn.phase];
  const playerHash = hashStr(state.turn.currentPlayer);
  const seed =
    (state.rngSeed ^
      Math.imul(state.turnNumber, 0x9e3779b9) ^
      phaseSalt ^
      playerHash) >>>
    0;
  return createPrng(seed);
}

// FNV-1a 32-bit. Stable, fast, no deps.
function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function personalityOf(state: GameState, player: PlayerId): AiPersonality {
  const p = state.players.find((x) => x.id === player);
  return p?.personality ?? DEFAULT_PERSONALITY;
}
